import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Kanban,
  AlertCircle,
  List,
  LayoutGrid,
  Search,
  Plus,
  User,
  Upload,
  X,
  CalendarPlus,
} from "lucide-react";
import { LossReasonModal } from "@/components/LossReasonModal";
import { NewProjectFromPipeline } from "@/components/NewProjectFromPipeline";
import MarkSignedModal from "@/components/pipeline/MarkSignedModal";

const STALE_ACCENT = "border-l-[#D99A2B]";
const NORMAL_ACCENT = "border-l-slate-700";

const STATUSES = [
  { key: "en_attente", label: "Nouveau" },
  { key: "contacte", label: "Contacté" },
  { key: "rdv_fixe", label: "Visite planifiée" },
  { key: "devis_envoye", label: "Devis envoyé" },
  { key: "en_negociation", label: "En négociation" },
  { key: "signe", label: "Signé" },
  { key: "perdu", label: "Perdu" },
];

const STATUS_LABELS = STATUSES.reduce((m, s) => ({ ...m, [s.key]: s.label }), {});

function fmtEur(n) {
  if (n === null || n === undefined) return null;
  return new Intl.NumberFormat("fr-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(Number(n));
}

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `il y a ${days} j`;
  return d.toLocaleDateString("fr-BE");
}

// --- Kanban Card ---------------------------------------------------------
function ProjectCard({ card, dragging, onOpen, onOpenContact, onOpenRdv, sourceLabel, memberLabel }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: card.id,
    data: { card },
  });
  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined;
  const accent = card.is_stale ? STALE_ACCENT : NORMAL_ACCENT;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        if (isDragging || dragging) return;
        e.stopPropagation();
        onOpen(card.id);
      }}
      role="button"
      tabIndex={0}
      data-testid={`pipeline-card-${card.id}`}
      className={`rounded-md border-l-4 ${accent} border border-slate-800 bg-slate-950/60 p-2.5 text-xs cursor-grab active:cursor-grabbing touch-none ${
        isDragging || dragging ? "opacity-40" : "hover:border-slate-600 hover:bg-slate-900"
      }`}
    >
      <div className="mb-1 flex items-start justify-between gap-2">
        <span className="font-semibold text-slate-100 leading-tight">
          {card.title || card.client_name}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {/* Turn 2b UX-C : accès rapide RDV depuis la carte Kanban */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenRdv(card.id);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            title="Fixer un rendez-vous"
            aria-label="Fixer un rendez-vous"
            className="rounded-md p-1 text-slate-400 hover:bg-[#C4553B]/15 hover:text-[#E07A62]"
            data-testid={`pipeline-card-rdv-${card.id}`}
          >
            <CalendarPlus className="h-3.5 w-3.5" />
          </button>
          {card.is_stale && (
            <span
              className="badge-warning inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px]"
              title={`Dans cette étape depuis ${card.stale_days} jours`}
              data-testid={`pipeline-card-stale-${card.id}`}
            >
              <AlertCircle className="h-3 w-3" />
              {card.stale_days}j
            </span>
          )}
        </div>
      </div>
      {card.contact_id ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenContact(card.contact_id);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="mb-1 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-[#E07A62]"
          data-testid={`pipeline-card-contact-${card.id}`}
        >
          <User className="h-3 w-3" /> {card.client_name}
        </button>
      ) : (
        <div className="mb-1 inline-flex items-center gap-1 text-[11px] text-slate-500">
          <User className="h-3 w-3" /> {card.client_name}
        </div>
      )}
      {card.project_type && (
        <div className="mb-1.5 line-clamp-2 text-slate-400">{card.project_type}</div>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {sourceLabel && (
          <Badge variant="outline" className="border-slate-700 bg-slate-800 text-[10px] text-slate-300">
            {sourceLabel}
          </Badge>
        )}
        {memberLabel && (
          <Badge variant="outline" className="border-slate-700 bg-slate-800/50 text-[10px] text-slate-400">
            {memberLabel}
          </Badge>
        )}
      </div>
      {card.amount_tvac !== null && card.amount_tvac !== undefined && (
        <div className="mt-1.5 tabular font-semibold text-[#B8C784]">{fmtEur(card.amount_tvac)}</div>
      )}
      {/* Turn UX-A A.3 — Badge acompte pour la colonne "Signé" */}
      {card.deposit && (
        <div className="mt-1.5">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}${card.deposit.pdf_download_url}`}
            target="_blank"
            rel="noopener noreferrer"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
              card.deposit.status === "payee"
                ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                : "border-[#C4553B]/50 bg-[#C4553B]/10 text-[#E07A62] hover:bg-[#C4553B]/20"
            }`}
            data-testid={`pipeline-card-deposit-${card.id}`}
            title={`Acompte ${card.deposit.number} · ${card.deposit.status} · cliquer pour ouvrir le PDF`}
          >
            Acompte {fmtEur(card.deposit.amount_tvac)} · {card.deposit.status}
          </a>
        </div>
      )}
      {card.last_activity_at && (
        <div className="mt-1 text-[10px] text-slate-500">MAJ {relTime(card.last_activity_at)}</div>
      )}
    </div>
  );
}

function PipelineColumn({ column, activeId, onOpenCard, onOpenContact, onOpenRdv, sources, members }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `col-${column.key}`,
    data: { columnKey: column.key },
  });
  return (
    <div
      ref={setNodeRef}
      data-testid={`pipeline-column-${column.key}`}
      className={`flex w-64 shrink-0 flex-col rounded-lg border ${
        isOver ? "border-[#C4553B] bg-slate-900" : "border-slate-800 bg-slate-900/40"
      }`}
    >
      <div className="border-b border-slate-800 px-3 py-2.5">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-300">
          {STATUS_LABELS[column.key] || column.label}
        </div>
        <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
          <span data-testid={`pipeline-col-count-${column.key}`}>
            {column.count} {column.count > 1 ? "projets" : "projet"}
          </span>
          <span className="tabular font-medium text-slate-400" data-testid={`pipeline-col-total-${column.key}`}>
            {fmtEur(column.total_ht) || "—"} HT
          </span>
        </div>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-2 min-h-[100px] max-h-[calc(100vh-320px)]">
        {column.cards.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-800 py-6 text-center text-[11px] text-slate-600">
            Aucun projet
          </div>
        ) : (
          column.cards.map((c) => (
            <ProjectCard
              key={c.id}
              card={c}
              dragging={activeId === c.id}
              onOpen={onOpenCard}
              onOpenContact={onOpenContact}
              onOpenRdv={onOpenRdv}
              sourceLabel={sources.find((s) => s.id === c.source_id)?.name}
              memberLabel={
                c.assigned_to_user_id
                  ? members.find((m) => m.id === c.assigned_to_user_id)?.first_name
                  : null
              }
            />
          ))
        )}
        {column.truncated && (
          <div className="pt-1 text-center text-[10px] italic text-slate-600">
            … + autres non affichées
          </div>
        )}
      </div>
    </div>
  );
}

// --- Main Pipeline Page ---------------------------------------------------
export default function PipelinePage() {
  const nav = useNavigate();
  const { state } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCard, setActiveCard] = useState(null);
  const [lostModal, setLostModal] = useState(null);

  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem("pipeline_view") || "kanban";
    } catch {
      return "kanban";
    }
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilters, setStatusFilters] = useState([]); // array of stage keys
  const [sourceFilter, setSourceFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [sources, setSources] = useState([]);
  const [members, setMembers] = useState([]);
  const [newProjOpen, setNewProjOpen] = useState(false);
  // Turn UX-A A.3 — Modal signature manuelle
  const [markSignedCard, setMarkSignedCard] = useState(null); // {id, title, client_name} ou null

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  useEffect(() => {
    try {
      localStorage.setItem("pipeline_view", view);
    } catch {
      /* ignore */
    }
  }, [view]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const [pipeRes, srcRes, memRes] = await Promise.all([
        api.get("/leads/pipeline"),
        api.get("/lead-sources?include_archived=false").catch(() => ({ data: [] })),
        api.get("/team/members").catch(() => ({ data: [] })),
      ]);
      setData(pipeRes.data);
      setSources((srcRes.data || []).filter((s) => s.active !== false));
      // BLOC 19/10 — Filtrage is_active : désactivés exclus du filtre "Assigné à"
      // du pipeline. Les cards déjà assignées à un désactivé gardent son nom.
      setMembers((memRes.data || []).filter((u) => u.is_active !== false));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Bug #2 fix (14/09/2026) — refetch quand company change dans le même onglet.
  }, [state?.company?.id]);

  // Applique tous les filtres (search + status + source + assignee + tag + période)
  const filteredData = useMemo(() => {
    if (!data) return null;
    const cutoff =
      periodFilter === "all" ? null : Date.now() - parseInt(periodFilter, 10) * 86400 * 1000;
    const q = debouncedSearch;
    const columns = data.columns.map((col) => {
      let cards = col.cards;
      if (q) {
        cards = cards.filter((c) =>
          [c.client_name, c.title, c.project_type]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q)),
        );
      }
      if (sourceFilter !== "all") {
        cards = cards.filter((c) => c.source_id === sourceFilter);
      }
      if (assigneeFilter !== "all") {
        if (assigneeFilter === "none") {
          cards = cards.filter((c) => !c.assigned_to_user_id);
        } else {
          cards = cards.filter((c) => c.assigned_to_user_id === assigneeFilter);
        }
      }
      if (tagFilter.trim()) {
        const t = tagFilter.trim().toLowerCase();
        cards = cards.filter((c) => (c.tags || []).some((x) => x.toLowerCase().includes(t)));
      }
      if (cutoff !== null) {
        cards = cards.filter((c) => new Date(c.created_at || c.last_activity_at).getTime() >= cutoff);
      }
      const count = cards.length;
      const total_ht = cards.reduce((s, x) => s + (x.amount_ht || 0), 0);
      return { ...col, cards, count, total_ht };
    });
    // status filters : si non vide, filtre les colonnes visibles
    const visibleCols = statusFilters.length > 0
      ? columns.filter((c) => statusFilters.includes(c.key))
      : columns;
    return { ...data, columns: visibleCols };
  }, [data, debouncedSearch, sourceFilter, assigneeFilter, tagFilter, periodFilter, statusFilters]);

  const flatItems = useMemo(() => {
    if (!filteredData) return [];
    const items = [];
    filteredData.columns.forEach((col) => {
      col.cards.forEach((c) => items.push({ ...c, stage: col.key }));
    });
    // tri : plus récent en premier
    items.sort((a, b) => {
      const ta = new Date(a.last_activity_at || a.stage_updated_at || 0).getTime();
      const tb = new Date(b.last_activity_at || b.stage_updated_at || 0).getTime();
      return tb - ta;
    });
    return items;
  }, [filteredData]);

  const totalCount = useMemo(() => flatItems.length, [flatItems]);

  const cardById = useMemo(() => {
    const m = {};
    (data?.columns || []).forEach((col) => col.cards.forEach((c) => (m[c.id] = { ...c, status: col.key })));
    return m;
  }, [data]);

  const onDragStart = (evt) => setActiveCard(cardById[evt.active.id] || null);

  const onDragEnd = async (evt) => {
    setActiveCard(null);
    const dropped = evt.active?.id;
    const over = evt.over?.data?.current?.columnKey;
    if (!dropped || !over) return;
    const card = cardById[dropped];
    if (!card || card.status === over) return;
    if (over === "perdu") {
      setLostModal({ leadId: card.id, leadName: card.client_name, prevStatus: card.status });
      return;
    }
    // Turn UX-A A.3 — Transition vers "signe" nécessite un devis + case acompte
    if (over === "signe") {
      setMarkSignedCard({
        id: card.id,
        title: card.title || card.client_name,
        client_name: card.client_name,
      });
      return;
    }
    const prev = data;
    setData((cur) => {
      if (!cur) return cur;
      const cols = cur.columns.map((c) => ({ ...c, cards: c.cards.filter((x) => x.id !== dropped) }));
      const srcCol = cur.columns.find((c) => c.key === card.status);
      const srcCard = srcCol?.cards.find((x) => x.id === dropped);
      const dstIdx = cols.findIndex((c) => c.key === over);
      if (dstIdx >= 0 && srcCard) {
        const newCard = { ...srcCard, stale_days: 0, is_stale: false };
        cols[dstIdx] = { ...cols[dstIdx], cards: [newCard, ...cols[dstIdx].cards] };
      }
      cols.forEach((col) => {
        col.count = col.cards.length;
        col.total_ht = col.cards.reduce((s, x) => s + (x.amount_ht || 0), 0);
      });
      return { ...cur, columns: cols };
    });
    try {
      await api.patch(`/leads/${dropped}`, { status: over });
      load();
      toast.success("Statut mis à jour");
    } catch (e) {
      toast.error(formatApiError(e));
      setData(prev);
    }
  };

  const openCard = (id) => nav(`/projects/${id}`);
  const openContact = (id) => nav(`/contacts/${id}`);
  // Turn 2b UX-C : ouvre directement le modal RDV du projet ciblé
  const openRdvForCard = (id) => nav(`/projects/${id}?open_rdv=1`);

  const toggleStatusFilter = (key) => {
    setStatusFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const clearAllFilters = () => {
    setSearch("");
    setStatusFilters([]);
    setSourceFilter("all");
    setAssigneeFilter("all");
    setTagFilter("");
    setPeriodFilter("all");
  };

  const hasActiveFilters =
    !!search ||
    statusFilters.length > 0 ||
    sourceFilter !== "all" ||
    assigneeFilter !== "all" ||
    !!tagFilter.trim() ||
    periodFilter !== "all";

  return (
    <div className="space-y-4" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 text-2xl font-bold text-slate-100">
            <Kanban className="h-5 w-5 text-[#C4553B]" /> Pipeline
            <Badge variant="outline" className="ml-1 border-slate-700 bg-slate-800 text-slate-300">
              {totalCount}
            </Badge>
          </h1>
          <p className="text-sm text-slate-400">
            Tous vos projets — glissez-déposez en Kanban ou naviguez en Liste.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-700 bg-slate-900 p-0.5" data-testid="pipeline-view-toggle">
            <button
              onClick={() => setView("kanban")}
              className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs ${
                view === "kanban" ? "bg-slate-800 text-slate-100" : "text-slate-400 hover:text-slate-200"
              }`}
              data-testid="view-kanban-btn"
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Kanban
            </button>
            <button
              onClick={() => setView("list")}
              className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs ${
                view === "list" ? "bg-slate-800 text-slate-100" : "text-slate-400 hover:text-slate-200"
              }`}
              data-testid="view-list-btn"
            >
              <List className="h-3.5 w-3.5" /> Liste
            </button>
          </div>
          <Button
            variant="outline"
            onClick={() => nav("/leads/import")}
            className="border-slate-700 bg-slate-800/50 text-slate-200 hover:bg-slate-800"
            data-testid="pipeline-import-tsv-btn"
          >
            <Upload className="mr-2 h-4 w-4" /> Importer TSV
          </Button>
          <Button
            onClick={() => setNewProjOpen(true)}
            className="bg-[#C4553B] text-white hover:bg-[#B44A32]"
            data-testid="btn-new-project-pipeline"
          >
            <Plus className="mr-2 h-4 w-4" /> Nouveau projet
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="space-y-3 p-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_180px_180px_180px_140px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Recherche titre projet, contact, description…"
                className="border-slate-700 bg-slate-950/50 pl-9 text-sm"
                data-testid="pipeline-search"
              />
            </div>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="border-slate-700 bg-slate-950/50 text-sm" data-testid="pipeline-source-filter">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                <SelectItem value="all">Toutes sources</SelectItem>
                {sources.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger className="border-slate-700 bg-slate-950/50 text-sm" data-testid="pipeline-assignee-filter">
                <SelectValue placeholder="Assigné" />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                <SelectItem value="all">Tous les membres</SelectItem>
                <SelectItem value="none">Non assignés</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              placeholder="Tag contact…"
              className="border-slate-700 bg-slate-950/50 text-sm"
              data-testid="pipeline-tag-filter"
            />
            <Select value={periodFilter} onValueChange={setPeriodFilter}>
              <SelectTrigger className="border-slate-700 bg-slate-950/50 text-sm" data-testid="pipeline-period-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                <SelectItem value="all">Toute période</SelectItem>
                <SelectItem value="7">7 j</SelectItem>
                <SelectItem value="30">30 j</SelectItem>
                <SelectItem value="90">90 j</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {STATUSES.map((s) => {
              const active = statusFilters.includes(s.key);
              return (
                <button
                  key={s.key}
                  onClick={() => toggleStatusFilter(s.key)}
                  className={`rounded-full border px-2.5 py-0.5 text-[11px] transition ${
                    active
                      ? "border-[#C4553B] bg-[#C4553B]/20 text-[#E9A98F]"
                      : "border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600"
                  }`}
                  data-testid={`filter-stage-${s.key}`}
                >
                  {s.label}
                </button>
              );
            })}
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="ml-1 inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-800/50 px-2 py-0.5 text-[11px] text-slate-400 hover:border-slate-600"
                data-testid="pipeline-clear-filters"
              >
                <X className="h-3 w-3" /> Effacer les filtres
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16" data-testid="pipeline-loading">
          <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
        </div>
      ) : view === "kanban" ? (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-kanban">
            {(filteredData?.columns || []).map((col) => (
              <PipelineColumn
                key={col.key}
                column={col}
                activeId={activeCard?.id}
                onOpenCard={openCard}
                onOpenContact={openContact}
                onOpenRdv={openRdvForCard}
                sources={sources}
                members={members}
              />
            ))}
          </div>
          <DragOverlay>
            {activeCard ? (
              <div className="w-64 rounded-md border-l-4 border-l-[#C4553B] border border-slate-700 bg-slate-900 p-2.5 text-xs shadow-2xl">
                <div className="font-semibold text-slate-100">{activeCard.title || activeCard.client_name}</div>
                {activeCard.project_type && (
                  <div className="mt-1 text-slate-400">{activeCard.project_type}</div>
                )}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <PipelineListView
          items={flatItems}
          sources={sources}
          members={members}
          onOpenCard={openCard}
          onOpenContact={openContact}
        />
      )}

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="p-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-[#D99A2B]"></span>Stale &gt; 7 jours dans l&apos;étape
          </span>
        </CardContent>
      </Card>

      <LossReasonModal
        open={!!lostModal}
        leadId={lostModal?.leadId}
        leadName={lostModal?.leadName}
        onDone={() => {
          setLostModal(null);
          load();
        }}
        onCancel={() => setLostModal(null)}
      />

      <NewProjectFromPipeline
        open={newProjOpen}
        onClose={() => setNewProjOpen(false)}
        onCreated={(project) => {
          setNewProjOpen(false);
          if (project?.id) nav(`/projects/${project.id}`);
          else load();
        }}
      />

      {/* Turn UX-A A.3 — Modal signature manuelle depuis drag pipeline */}
      <MarkSignedModal
        open={!!markSignedCard}
        card={markSignedCard}
        onOpenChange={(open) => {
          if (!open) setMarkSignedCard(null);
        }}
        onSuccess={() => {
          setMarkSignedCard(null);
          load();
        }}
      />
    </div>
  );
}

// --- List View -----------------------------------------------------------
function PipelineListView({ items, sources, members, onOpenCard, onOpenContact }) {
  const [sortKey, setSortKey] = useState("updated");
  const [sortDir, setSortDir] = useState("desc");
  const sorted = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      let va, vb;
      switch (sortKey) {
        case "title":
          va = (a.title || a.client_name || "").toLowerCase();
          vb = (b.title || b.client_name || "").toLowerCase();
          break;
        case "contact":
          va = (a.client_name || "").toLowerCase();
          vb = (b.client_name || "").toLowerCase();
          break;
        case "stage":
          va = a.stage || "";
          vb = b.stage || "";
          break;
        case "amount":
          va = a.amount_tvac || 0;
          vb = b.amount_tvac || 0;
          break;
        default:
          va = new Date(a.last_activity_at || a.stage_updated_at || 0).getTime();
          vb = new Date(b.last_activity_at || b.stage_updated_at || 0).getTime();
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  const changeSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  if (items.length === 0) {
    return (
      <Card className="border-dashed border-slate-800 bg-transparent">
        <CardContent className="flex flex-col items-center justify-center py-14 text-center">
          <List className="mb-3 h-10 w-10 text-slate-600" />
          <p className="text-sm text-slate-300">Aucun projet ne correspond aux filtres.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-800 bg-slate-900/40">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="pipeline-list-table">
          <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="cursor-pointer px-4 py-3 hover:text-slate-300" onClick={() => changeSort("title")}>
                Titre projet
              </th>
              <th className="cursor-pointer px-4 py-3 hover:text-slate-300" onClick={() => changeSort("contact")}>
                Contact
              </th>
              <th className="cursor-pointer px-4 py-3 hover:text-slate-300" onClick={() => changeSort("stage")}>
                Statut
              </th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Assigné</th>
              <th className="cursor-pointer px-4 py-3 text-right hover:text-slate-300" onClick={() => changeSort("amount")}>
                Montant TVAC
              </th>
              <th className="cursor-pointer px-4 py-3 hover:text-slate-300" onClick={() => changeSort("updated")}>
                Dernière activité
              </th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => {
              const src = sources.find((s) => s.id === p.source_id);
              const mem = members.find((m) => m.id === p.assigned_to_user_id);
              return (
                <tr key={p.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`pipeline-list-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onOpenCard(p.id)}
                      className="text-left font-medium text-slate-100 hover:text-[#E07A62]"
                      data-testid={`pipeline-list-title-${p.id}`}
                    >
                      {p.title || p.client_name}
                    </button>
                    {p.is_stale && (
                      <span
                        className="badge-warning ml-2 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px]"
                        title={`${p.stale_days}j sans mouvement`}
                      >
                        <AlertCircle className="h-3 w-3" /> {p.stale_days}j
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-300">
                    {p.contact_id ? (
                      <button
                        onClick={() => onOpenContact(p.contact_id)}
                        className="inline-flex items-center gap-1 hover:text-[#E07A62]"
                        data-testid={`pipeline-list-contact-${p.id}`}
                      >
                        <User className="h-3 w-3" /> {p.client_name}
                      </button>
                    ) : (
                      <span className="text-slate-500">{p.client_name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="border-slate-700 bg-slate-800 text-[10px] text-slate-300">
                      {STATUS_LABELS[p.stage] || p.stage}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{src?.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-400">{mem ? `${mem.first_name} ${mem.last_name}` : "—"}</td>
                  <td className="px-4 py-3 text-right font-medium tabular text-[#B8C784]">
                    {fmtEur(p.amount_tvac) || <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-400">{relTime(p.last_activity_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenCard(p.id)}
                      className="border-slate-700 bg-slate-800/50 text-slate-200"
                      data-testid={`pipeline-list-edit-${p.id}`}
                    >
                      Ouvrir
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
